import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PublicSiteHeader } from '@/components/public/PublicSiteHeader';
import { publicDemoModel } from '@/data/publicDemoModel';
import { subscriptionPlanService } from '@/services/subscriptionPlanService';
import type { BillingPeriod, SubscriptionPlan, SupportedCurrency } from '@/types/subscriptionPlans';
import { formatPlanPrice, getPlanPrice, SUPPORTED_CURRENCIES } from '@/utils/subscriptionPlans';

const PublicLandingPage: React.FC = () => {
  const { t } = useTranslation('landing');
  const { t: tKpi } = useTranslation('kpi');

  const [plans, setPlans] = React.useState<SubscriptionPlan[]>([]);
  const [plansLoading, setPlansLoading] = React.useState(true);
  const [plansError, setPlansError] = React.useState<string | null>(null);
  const [plansSource, setPlansSource] = React.useState<'firestore' | 'fallback'>('firestore');
  const [pricingPeriod, setPricingPeriod] = React.useState<BillingPeriod>('monthly');
  const [currency, setCurrency] = React.useState<SupportedCurrency>('USD');

  const teaserMetrics = [
    { label: t('multiBankCustomers'), value: `${publicDemoModel.executiveSnapshot.multiBankShare}%` },
    { label: t('avgBanksPerCustomer'), value: String(publicDemoModel.executiveSnapshot.avgBanksPerCustomer) },
    { label: t('primaryBankShare'), value: `${publicDemoModel.executiveSnapshot.primaryBankLeaderShare}%` },
    { label: t('topSecondChoiceCompetitor'), value: publicDemoModel.executiveSnapshot.topSecondChoiceCompetitor },
  ];

  const benefits = [
    t('benefit1'),
    t('benefit2'),
    t('benefit3'),
    t('benefit4'),
    t('benefit5'),
  ];

  const getPlanJourney = (plan: SubscriptionPlan): { label: string; target: string } => {
    if (plan.entitlementMapping.tier === 'free') {
      return { label: t('startFreeAccess'), target: '/get-started?plan=free' };
    }
    if (plan.entitlementMapping.aiAddon) {
      return { label: t('discussPremiumAccess'), target: '/get-started?plan=premium' };
    }
    return { label: t('requestStandardAccess'), target: '/get-started?plan=standard' };
  };

  React.useEffect(() => {
    let isMounted = true;

    const loadPlans = async () => {
      try {
        const result = await subscriptionPlanService.listPublicPlansWithFallback();
        if (isMounted) {
          setPlans(result.plans);
          setPlansSource(result.source);
          setPlansError(result.fallbackReason);
        }
      } catch (_err) {
        if (isMounted) {
          setPlans([]);
          setPlansSource('fallback');
          setPlansError(t('subscriptionPricingLoadError'));
        }
      } finally {
        if (isMounted) {
          setPlansLoading(false);
        }
      }
    };

    void loadPlans();

    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-[#0F172A]">
      <PublicSiteHeader />

      <main className="mx-auto max-w-6xl px-6 py-12">

        {/* ── Hero ── */}
        <section className="rounded-2xl border border-[#E2E8F0] bg-white p-10 shadow-sm">
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#E10613]">
            {t('bankingIntelligence')}
          </div>
          <h1 className="mt-6 text-4xl font-black leading-tight text-[#0F172A] md:text-5xl">
            {t('heroHeadline')}
          </h1>
          <p className="mt-5 max-w-4xl text-base text-[#475569] md:text-lg">
            {t('heroSub')}
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/get-started"
              className="rounded-lg bg-[#E10613] px-6 py-3 text-xs font-bold uppercase tracking-widest text-white hover:bg-[#C1121F]"
            >
              {t('getStarted')}
            </Link>
            <Link
              to="/demo"
              className="rounded-lg border border-[#E2E8F0] bg-white px-6 py-3 text-xs font-bold uppercase tracking-widest text-[#475569] hover:border-[#CBD5E1] hover:text-[#0F172A]"
            >
              {t('viewSampleInsights')}
            </Link>
          </div>
          <p className="mt-6 inline-block rounded-lg border border-[#FCA5A5] bg-[#FEF2F2] px-4 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] text-[#E10613]">
            {t('alwaysOnVisibility')}
          </p>
        </section>

        {/* ── Executive snapshot metrics ── */}
        <section className="mt-12" id="insights">
          <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-[#94A3B8]">{t('executiveSnapshot')}</h2>
          <p className="mt-1.5 text-sm text-[#64748B]">
            {t('illustrativeDemoNote', { country: publicDemoModel.country })}
          </p>
          <div className="mt-5 grid gap-4 md:grid-cols-4">
            {teaserMetrics.map((metric) => (
              <article key={metric.label} className="rounded-xl border border-[#E2E8F0] bg-white p-5 shadow-sm">
                <p className="text-[10px] font-semibold uppercase tracking-widest text-[#64748B]">{metric.label}</p>
                <p className="mt-3 text-3xl font-black text-[#0F172A]">{metric.value}</p>
              </article>
            ))}
          </div>
        </section>

        {/* ── Methodology + Coverage ── */}
        <section className="mt-8 grid gap-4 lg:grid-cols-2">
          <article className="rounded-xl border border-[#E2E8F0] bg-white p-6 shadow-sm">
            <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#E10613]">{t('methodology')}</p>
            <h2 className="mt-2 text-xl font-black text-[#0F172A]">{t('decisionGradeMeasurement')}</h2>
            <p className="mt-3 text-sm text-[#475569]">
              {t('methodologyDescription')}
            </p>
            <Link
              to="/methodology"
              className="mt-5 inline-flex rounded-lg border border-[#E2E8F0] px-4 py-2 text-xs font-bold uppercase tracking-widest text-[#475569] hover:border-[#CBD5E1] hover:text-[#0F172A]"
            >
              {t('viewMethodology')}
            </Link>
          </article>
          <article className="rounded-xl border border-[#E2E8F0] bg-white p-6 shadow-sm">
            <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#E10613]">{t('coverage')}</p>
            <h2 className="mt-2 text-xl font-black text-[#0F172A]">{t('continuousIntelligence')}</h2>
            <p className="mt-3 text-sm text-[#475569]">
              {t('coverageDescription')}
            </p>
            <Link
              to="/coverage"
              className="mt-5 inline-flex rounded-lg border border-[#E2E8F0] px-4 py-2 text-xs font-bold uppercase tracking-widest text-[#475569] hover:border-[#CBD5E1] hover:text-[#0F172A]"
            >
              {t('viewCoverage')}
            </Link>
          </article>
        </section>

        {/* ── Competitive landscape + BrandEdge preview ── */}
        <section className="mt-8 grid gap-4 lg:grid-cols-[1.1fr_0.9fr]">
          <div className="rounded-xl border border-[#E2E8F0] bg-white p-6 shadow-sm">
            <div className="mb-2 inline-flex rounded-md bg-[#FEF2F2] px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest text-[#E10613]">
              {t('competitive')}
            </div>
            <h2 className="mt-2 text-xl font-black text-[#0F172A]">{t('understandCompetitiveLandscape')}</h2>
            <p className="mt-3 text-sm text-[#475569]">
              {t('trackCustomerMovement')}
            </p>
            <Link
              to="/get-started?plan=standard"
              className="mt-6 inline-flex rounded-lg bg-[#E10613] px-5 py-2.5 text-xs font-bold uppercase tracking-widest text-white hover:bg-[#C1121F]"
            >
              {t('registerToSeeMore')}
            </Link>
          </div>

          <div className="rounded-xl border border-[#E2E8F0] bg-white p-5 shadow-sm">
            <div className="rounded-lg border border-[#E2E8F0] bg-[#F8FAFC] p-4">
              <div className="flex items-center justify-between text-[10px] font-semibold uppercase tracking-widest text-[#64748B]">
                <span>{t('brandEdgePreview')}</span>
                <span>{t('demoMarketLabel', { country: publicDemoModel.country })}</span>
              </div>
              <p className="mt-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-[#94A3B8]">
                {t('illustrativeMarketNote')}
              </p>
              <div className="mt-4 grid grid-cols-3 gap-3 text-xs">
                <div className="rounded-lg border border-[#FCA5A5] bg-[#FEF2F2] p-3">
                  <p className="text-[#64748B]">{tKpi('awareness')}</p>
                  <p className="mt-1 text-xl font-black text-[#E10613]">{publicDemoModel.brandEdgePreview.awareness}%</p>
                </div>
                <div className="rounded-lg border border-[#E2E8F0] bg-white p-3">
                  <p className="text-[#64748B]">{tKpi('currentUsage')}</p>
                  <p className="mt-1 text-xl font-black text-[#0F172A]">{publicDemoModel.brandEdgePreview.currentUsage}%</p>
                </div>
                <div className="rounded-lg border border-[#E2E8F0] bg-white p-3">
                  <p className="text-[#64748B]">{tKpi('loyaltyIndex')}</p>
                  <p className="mt-1 text-xl font-black text-[#0F172A]">{publicDemoModel.brandEdgePreview.loyaltyIndex}%</p>
                </div>
              </div>
              <div className="mt-4 space-y-2 text-xs text-[#475569]">
                <div className="flex items-center justify-between border-t border-[#F1F5F9] pt-2"><span>{t('multiBankShareLabel')}</span><span className="font-semibold text-[#0F172A]">{publicDemoModel.brandEdgePreview.multiBankShare}%</span></div>
                <div className="flex items-center justify-between"><span>{t('secondChoicePressure')}</span><span className="font-semibold text-[#0F172A]">{publicDemoModel.brandEdgePreview.secondChoicePressureLabel}</span></div>
                <div className="flex items-center justify-between"><span>{t('momentumTrend')}</span><span className="font-semibold text-[#E10613]">{t('momentumTrendValue', { pts: publicDemoModel.brandEdgePreview.momentumTrend })}</span></div>
              </div>
            </div>
          </div>
        </section>

        {/* ── Why banks use BrandEdge ── */}
        <section className="mt-8 rounded-xl border border-[#E2E8F0] bg-white p-6 shadow-sm">
          <div className="mb-1 text-[10px] font-bold uppercase tracking-[0.2em] text-[#E10613]">{t('value')}</div>
          <h2 className="text-xl font-black text-[#0F172A]">{t('whyBanksUseBrandEdge')}</h2>
          <div className="mt-5 grid gap-3 md:grid-cols-2">
            {benefits.map((benefit) => (
              <p key={benefit} className="flex items-start gap-2.5 rounded-lg border border-[#E2E8F0] bg-[#F8FAFC] px-4 py-3 text-sm text-[#344054]">
                <span className="mt-0.5 flex-shrink-0 text-[#E10613]">✓</span>
                {benefit}
              </p>
            ))}
          </div>
        </section>

        {/* ── Pricing ── */}
        <section className="mt-8 rounded-xl border border-[#E2E8F0] bg-white p-6 shadow-sm">
          <div className="mb-1 text-[10px] font-bold uppercase tracking-[0.2em] text-[#E10613]">{t('pricing')}</div>
          <h2 className="text-xl font-black text-[#0F172A]">{t('choosePlan')}</h2>
          <p className="mt-2 text-sm text-[#475569]">
            {t('choosePlanSub')}
          </p>
          {!plansLoading && plansSource === 'fallback' && (
            <div className="mt-5 rounded-lg border border-amber-200 bg-amber-50 px-5 py-4 text-sm text-amber-800">
              {t('liveSubscriptionUnavailable')}
              {plansError ? ` (${plansError})` : ''}
            </div>
          )}
          <div className="mt-5 flex flex-wrap items-center gap-3">
            <div className="inline-flex rounded-lg border border-[#E2E8F0] bg-[#F8FAFC] p-1">
              {(['monthly', 'annual'] as const).map((period) => (
                <button
                  key={period}
                  onClick={() => setPricingPeriod(period)}
                  className={`rounded-md px-4 py-2 text-xs font-bold uppercase tracking-widest transition-colors ${
                    pricingPeriod === period
                      ? 'bg-[#E10613] text-white'
                      : 'text-[#475569] hover:text-[#0F172A]'
                  }`}
                >
                  {t(period)}
                </button>
              ))}
            </div>
            <div className="inline-flex rounded-lg border border-[#E2E8F0] bg-[#F8FAFC] p-1">
              {SUPPORTED_CURRENCIES.map((supportedCurrency) => (
                <button
                  key={supportedCurrency}
                  onClick={() => setCurrency(supportedCurrency)}
                  className={`rounded-md px-4 py-2 text-xs font-bold uppercase tracking-widest transition-colors ${
                    currency === supportedCurrency
                      ? 'bg-white text-[#0F172A] shadow-sm'
                      : 'text-[#475569] hover:text-[#0F172A]'
                  }`}
                >
                  {supportedCurrency}
                </button>
              ))}
            </div>
          </div>

          {plansLoading ? (
            <div className="mt-5 rounded-lg border border-[#E2E8F0] bg-[#F8FAFC] px-5 py-6 text-sm text-[#94A3B8]">
              {t('loadingPlans')}
            </div>
          ) : plans.length === 0 ? (
            <div className="mt-5 rounded-lg border border-dashed border-[#E2E8F0] bg-[#F8FAFC] px-5 py-6">
              <p className="text-sm text-[#475569]">{t('subscriptionBeingConfigured')}</p>
              <p className="mt-2 text-xs text-[#94A3B8]">
                {t('plansPendingNote')}
              </p>
              <Link
                to="/get-started"
                className="mt-4 inline-flex rounded-lg bg-[#E10613] px-4 py-2 text-[11px] font-bold uppercase tracking-widest text-white hover:bg-[#C1121F]"
              >
                {t('getStarted')}
              </Link>
            </div>
          ) : (
            <div className="mt-5 grid gap-4 lg:grid-cols-3">
              {plans.map((plan) => {
                const journey = getPlanJourney(plan);
                return (
                  <article
                    key={plan.id}
                    className={`rounded-xl border p-5 ${
                      plan.featured
                        ? 'border-[#E10613] bg-[#FEF2F2]'
                        : 'border-[#E2E8F0] bg-[#F8FAFC]'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <h3 className="text-base font-black uppercase tracking-wide text-[#0F172A]">{plan.publicName}</h3>
                        <p className="mt-2 text-sm text-[#475569]">{plan.positioningLine}</p>
                      </div>
                      {plan.featured && (
                        <span className="rounded-full border border-[#FCA5A5] bg-[#E10613] px-3 py-1 text-[10px] font-bold uppercase tracking-[0.2em] text-white">
                          {t('featured')}
                        </span>
                      )}
                    </div>
                    <div className="mt-5">
                      <p className="text-3xl font-black text-[#0F172A]">
                        {formatPlanPrice(getPlanPrice(plan, pricingPeriod, currency), currency)}
                      </p>
                      <p className="mt-1 text-xs uppercase tracking-[0.18em] text-[#94A3B8]">
                        {t('pricingNote', { period: pricingPeriod, currency })}
                      </p>
                    </div>
                    <div className="mt-4 space-y-2">
                      {plan.benefits.map((feature) => (
                        <p key={`${plan.id}-${feature}`} className="text-xs text-[#475569]">— {feature}</p>
                      ))}
                    </div>
                    <Link
                      to={journey.target}
                      className={`mt-5 inline-flex rounded-lg px-4 py-2 text-[11px] font-bold uppercase tracking-widest ${
                        plan.featured
                          ? 'bg-[#E10613] text-white hover:bg-[#C1121F]'
                          : 'border border-[#E2E8F0] bg-white text-[#344054] hover:border-[#CBD5E1]'
                      }`}
                    >
                      {journey.label}
                    </Link>
                  </article>
                );
              })}
            </div>
          )}
        </section>

        {/* ── From periodic research ── */}
        <section className="mt-8 rounded-xl border border-[#E2E8F0] bg-white p-6 shadow-sm">
          <h2 className="text-xl font-black text-[#0F172A]">
            {t('fromPeriodicResearch')}
          </h2>
          <p className="mt-3 text-sm text-[#475569]">
            {t('fromPeriodicResearchSub')}
          </p>
        </section>

        {/* ── What banks learn ── */}
        <section className="mt-8 rounded-xl border border-[#FCA5A5] bg-[#FEF2F2] p-6">
          <div className="mb-1 text-[10px] font-bold uppercase tracking-[0.2em] text-[#E10613]">{t('insights')}</div>
          <h2 className="text-xl font-black text-[#0F172A]">
            {t('whatBanksLearn')}
          </h2>
          <div className="mt-5 grid gap-3 md:grid-cols-2">
            {([
              { assumption: t('assumption1'), reality: t('reality1') },
              { assumption: t('assumption2'), reality: t('reality2') },
              { assumption: t('assumption3'), reality: t('reality3') },
              { assumption: t('assumption4'), reality: t('reality4') },
            ] as const).map(({ assumption, reality }) => (
              <article key={assumption} className="rounded-xl border border-[#E2E8F0] bg-white p-4">
                <p className="text-[10px] font-bold uppercase tracking-widest text-[#94A3B8]">{t('assumption')}</p>
                <p className="mt-2 text-sm font-medium text-[#0F172A]">{assumption}</p>
                <p className="mt-2 text-xs text-[#E10613]">{reality}</p>
              </article>
            ))}
          </div>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link
              to="/get-started?plan=standard"
              className="rounded-lg bg-[#E10613] px-5 py-3 text-xs font-bold uppercase tracking-widest text-white hover:bg-[#C1121F]"
            >
              {t('getStarted')}
            </Link>
            <Link
              to="/demo"
              className="rounded-lg border border-[#FCA5A5] bg-white px-5 py-3 text-xs font-bold uppercase tracking-widest text-[#E10613] hover:bg-[#FEF2F2]"
            >
              {t('viewSampleInsights')}
            </Link>
          </div>
        </section>

        {/* ── Survey participation ── */}
        <section className="mt-8 rounded-xl border border-[#E2E8F0] bg-white p-6 shadow-sm">
          <h2 className="text-xl font-black text-[#0F172A]">{t('surveyParticipation')}</h2>
          <p className="mt-3 text-sm text-[#475569]">
            {t('surveyParticipationSub')}
          </p>
          <Link
            to="/survey"
            className="mt-5 inline-flex rounded-lg border border-[#E2E8F0] px-5 py-2.5 text-xs font-bold uppercase tracking-widest text-[#475569] hover:border-[#CBD5E1] hover:text-[#0F172A]"
          >
            {t('takeTheSurvey')}
          </Link>
        </section>

      </main>
    </div>
  );
};

export default PublicLandingPage;
